import React from 'react';
import { theme } from '../../../../../common/styles/theme';
import { Box, Stack } from '@mui/material';
import { alpha, SxProps } from '@mui/system';
import { ITargetCaptionItemProps } from './types';
import { DeviceContext } from '../../../../../common/contexts/DeviceContext';
import { PlayButton } from '../../../PlayButton/components/PlayButton';
import { SPEAKER_COLORS } from '../constants';

export const TargetCaptionItem: React.FC<ITargetCaptionItemProps> = ({
	index,
	isActive,
	startTime,
	endTime,
	captionText,
	playerActive,
	onPlayPauseClick,
	onItemDblClick,
	showPlayPause,
	onMouseEnter,
	onMouseLeave,
	onSetHeight,
	showTranslations,
	userName,
	userColorIndex,
}) => {
	const { isFullScreen } = React.useContext(DeviceContext);

	const speakerColor = userColorIndex !== undefined && userColorIndex >= 0
		? SPEAKER_COLORS[userColorIndex % SPEAKER_COLORS.length]
		: theme.palette.grey[300];

	const rootElemRef = React.useRef<HTMLDivElement>(null);

	if (onSetHeight && rootElemRef.current) {
		const height = rootElemRef.current.offsetHeight;
		if (height) {
			onSetHeight(index, height);
		}
	}

	React.useEffect(() => {
		const setHeight = () => {
			if (onSetHeight && rootElemRef.current) {
				const height = rootElemRef.current.offsetHeight;
				if (height) {
					onSetHeight(index, height);
				}
			}
		}

		if (onSetHeight && rootElemRef.current) {
			setHeight();
			new ResizeObserver(setHeight).observe(rootElemRef.current);
		}
	}, [isActive]);

	const elStyle: SxProps = {
		position: 'relative',
		zIndex: 10,
		width: '100%',
		py: theme.spacing(.5),
		lineHeight: theme.spacing(2.5),
		fontWeight: 400,
		fontSize: theme.spacing(2),

		'&.translated': {
			py: 0,
			lineHeight: theme.spacing(3.5),
		},

		'.root-inner.fullscreen &': {
			lineHeight: `1.7vw`,
		},

		'.mobile .root-inner.fullscreen &': {
			lineHeight: `3.4vw`,
		},
	};

	let boxStyle: SxProps = {
		position: 'relative',
		pr: theme.spacing(1.5),
		transition: 'opacity .3s ease',

		'&:hover': {
			'.caption-item__play-icon': {
				opacity: .4,
			},

			'.caption-item__user': {
				opacity: 0,
			},
		},

		'&.active': {
			zIndex: 100,

			'.caption-item__play': {
				background: `${theme.palette.primary[100]}`,
			},

			'.caption-item__play-icon': {
				opacity: 1,
			},
		},

		'.root-inner.fullscreen &': {
			pl: theme.spacing(1.5),
			color: alpha(theme.palette.common.white, .5),
			background: theme.palette.common.black,

			'&.active': {
				color: theme.palette.common.white,
			},
		},

		'.mobile & .caption-item__play-icon': {
          pointerEvents: 'all',
        },

		'&.user': {
			pt: theme.spacing(1.5),
		},
	};

	const handlePlayPauseClick = () => {
		onPlayPauseClick(index);
	}

	const handleDoubleClick = () => {
		onItemDblClick(index, -1);
	}

	const handleMouseEnter = () => {
		onMouseEnter(index);
	}
	const handleMouseLeave = () => {
		onMouseLeave(index);
	}

	return (
		<Stack
			id={`caption-item-${index}`}
			ref={rootElemRef}
			className={`
				caption-item
				${isActive ? 'active' : ''}
			`}
			direction={'row'}
			sx={boxStyle}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			{!!userName &&
				<Box
					className={`caption-item__user color-${userColorIndex}`}
					sx={{
						position: 'absolute',
						zIndex: 0,
						left: theme.spacing(-2.5),
						top: theme.spacing(1),
						color: speakerColor,
						fontSize: '10px',
						overflow: 'hidden',
						maxWidth: theme.spacing(2.5),
						userSelect: 'none',
						pointerEvents: 'none',
					}}
				>
					{userName}
				</Box>
			}

			{!isFullScreen && showPlayPause &&
				<Box
					className={`caption-item__play`}
					sx={{
						position: 'absolute',
						zIndex: 0,
						left: theme.spacing(-4.25),
						right: 0,
						top: 0,
						bottom: 0,
						borderRadius: theme.spacing(2),

						'.caption-item__play-icon': {
							position: 'absolute',
							left: '1px',
							top: '1px',
							opacity: 0,

							'.caption-item:hover &:hover': {
								opacity: 1,
							},
						},
					}}
				>
					<PlayButton
						className={`caption-item__play-icon`}
						isPlay={playerActive}
						onClick={handlePlayPauseClick}
					/>
				</Box>
			}

			<Box
				className={`caption-item__letters ${showTranslations ? 'translated' : ''}`}
				sx={elStyle}
				data-start={startTime}
				data-end={endTime}
				data-caption-index={index}
				onDoubleClick={handleDoubleClick}
			>
				<span className="highlighted-span">{captionText}</span>
			</Box>
		</Stack>
	);

};
